import { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import NvFileUploadProgress from 'shared/components/nv-file-upload-progress';
import NvInlineUploadedFile from 'shared/components/nv-uploaded-file/nv-inline-uploaded-file';
import type { FileProps } from 'shared/components/nv-uploaded-file/nv-inline-uploaded-file';
import useUploadFile, { NovoEdFile } from 'shared/hooks/use-upload-file';
import { S3NameSpaces } from 'shared/services/s3-upload-factory';
import { useAppDispatch } from 'redux/store';
import {
  type UploadAdditionalResourceResponse,
  uploadAdditionalResources,
  updateAdditionalResource,
  removeAdditionalResource,
} from 'redux/actions/help-options';
import { getCurrentCourse } from 'redux/selectors/course';
import { AdditionalResourceStatus, type AdditionalResource } from 'redux/schemas/models/help-options';

interface ResourceListItemProps {
  resource: AdditionalResource;
  onUploadError: (errorMessage: string) => void;
}

const toFileProps = (resource: AdditionalResource): FileProps => ({
  id: String(resource.id),
  uniqueId: resource.uniqueId || '',
  fileName: resource.name,
  fileSize: resource.size,
  contentType: resource.type,
  url: resource.url,
});

const ResourceListItem = ({ resource, onUploadError }: ResourceListItemProps) => {
  const dispatch = useAppDispatch();
  const { catalogId } = useSelector(getCurrentCourse) as { catalogId: string };
  const [deletingResourceId, setDeletingResourceId] = useState<string | null>(null);
  const hasStartedUploadRef = useRef<boolean>(false);

  const { uploadFiles, abortUpload, isUploading, filesUploading } = useUploadFile({
    keepProgressAfterComplete: true,
  });

  const isFileUploading = resource.status === AdditionalResourceStatus.UPLOADING;

  const uploadFile = useCallback(
    async (uploadingFile: File) => {
      try {
        const uploadedFiles = await uploadFiles([uploadingFile], S3NameSpaces.ATTACHMENTS);
        const uploadedFile: NovoEdFile = uploadedFiles[0];

        if (!uploadedFile?.uniqueId) {
          dispatch(removeAdditionalResource(resource.id));
          return;
        }

        const formattedResource: FileProps = {
          id: String(resource.id),
          uniqueId: uploadedFile.uniqueId,
          fileName: uploadedFile.name,
          fileSize: uploadedFile.size,
          contentType: uploadedFile.type,
          url: uploadedFile.url,
        };

        try {
          const response: UploadAdditionalResourceResponse[] = await dispatch(
            uploadAdditionalResources({
              catalogId,
              additionalResources: [formattedResource],
            }),
          ).unwrap();

          const uploadedResource = response[0];

          if (uploadedResource?.errors) {
            onUploadError(uploadedResource.errors);
            dispatch(removeAdditionalResource(resource.id));
            return;
          }

          dispatch(
            updateAdditionalResource(resource.uniqueId!, {
              name: uploadedFile.name,
              size: uploadedFile.size,
              type: uploadedFile.type,
              id: uploadedResource?.resourceId,
              uniqueId: uploadedResource?.resourceId?.toString(),
              url: uploadedResource?.url || formattedResource.url,
              status: AdditionalResourceStatus.SUCCESS,
              uploadedFile: undefined,
            }),
          );
        } catch (error) {
          onUploadError(
            t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.UNABLE_TO_UPLOAD(),
          );
          dispatch(removeAdditionalResource(resource.id));
        }
      } catch (error) {
        onUploadError(
          t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.UNABLE_TO_UPLOAD(),
        );
        dispatch(removeAdditionalResource(resource.id));
      }
    },
    [catalogId, dispatch, uploadFiles, onUploadError, resource.uniqueId, resource.id],
  );

  useEffect(() => {
    if (
      resource.status === AdditionalResourceStatus.UPLOADING
      && resource.uploadedFile
      && !hasStartedUploadRef.current
    ) {
      hasStartedUploadRef.current = true;
      uploadFile(resource.uploadedFile);
    }
  }, [resource.status, resource.uploadedFile, uploadFile]);

  const handleDeleteResource = () => {
    dispatch(removeAdditionalResource(resource.id));
  };

  if (isFileUploading) {
    const fileUploading = Object.values(filesUploading)[0];
    return (
      <div className='mt-3'>
        <NvFileUploadProgress
          id={resource.id}
          hasUploadPercentage
          uploadInProgress={isUploading}
          fileUploading={fileUploading}
          abortUpload={abortUpload}
          showFileName={false}
          isInlineContent
        />
      </div>
    );
  }

  const isProcessing = resource.status === AdditionalResourceStatus.PROCESSING;

  return (
    <NvInlineUploadedFile
      resource={toFileProps(resource)}
      onDelete={handleDeleteResource}
      deletingResourceId={deletingResourceId}
      setDeletingResourceId={setDeletingResourceId}
      deletePopoverText={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.DELETE_POPOVER()}
      isProcessing={isProcessing}
    />
  );
};

export default ResourceListItem;

